import useDiscoveryFetch from './useDiscoveryFetch';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import api from '../../api/axiosInstance';
import { useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
} from 'react-bootstrap';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import icQ from '../../assets/img/ic-q.svg';
import icA from '../../assets/img/ic-a.svg';
import NavTabs from '../../NavTabs';
import CountryDetailHero from './countryDetail/CountryDetailHero';
import CountryDetailMobileTabs from './countryDetail/CountryDetailMobileTabs';
import getCountryDetailNavItems from './countryDetail/countryDetailNavItems';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';

const DiscoveryCountryViewFinancials = () => {
    const { id } = useParams();
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });

    const [countryData, setCountryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCountry = async () => {
            try {
                if (!isReconnect || !countryData) setLoading(true);
                setError('');
                const response = await api.get(`/api/discovery/countries/${id}`, { signal });
                if (!isCurrent()) return;
                setCountryData(response.data);
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching country:', err);
                setError('An error occurred while fetching country details.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchCountry();
    }, [id]);

    const formData = {
        countryName: countryData?.countryName || countryData?.country?.name || '',
        url: countryData?.url || '',
        cityCount: countryData?.cityCount || 0,
        currency: countryData?.currency || '',
    };

    const discoveryCountryNavItems = getCountryDetailNavItems(id);

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-country',
        discoveryType: 'countries',
        countryId: id,
        countryName: countryData?.countryName || countryData?.country?.name || '',
        activeTab: 'Financials & Banks',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'countries',
            selectedCountry: countryData?.countryName || countryData?.country?.name || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!countryData) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">Country not found.</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    const sectionContent = (
        <>
            <Row className={isMobile ? undefined : 'mb-4'}>
                <Col>
                    <Card className="h-100">
                        <Card.Body className={isMobile ? 'pb-0' : undefined}>
                            <Row>
                                <Col md={12}>
                                    <h3><span className="item-number">5.</span> Financials & Banks</h3>
                                    <hr />
                                                                    </Col>
                                                                </Row>

                                                                <Row className={isMobile ? undefined : 'mb-3'}>
                                                                    <Col md={6}>
                                                                        <Card>
                                                                            <Card.Body className="p-3">
                                                                                <div className="icon-text align-items-center">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icQ} alt="What documents are generally required to open a bank account?" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        What documents are generally required to open a bank account?
                                                                                    </span>
                                                                                </div>
                                                                            </Card.Body>
                                                                        </Card>
                                                                        <Row className="sub-card-row">
                                                                            <Col className="justify-content-start">
                                                                                <Card>
                                                                                    <Card.Body>
                                                                                        <div className="icon-text align-items-center">
                                                                                            <span className="icon" aria-hidden="true">
                                                                                                <Image src={icA} alt="What documents are generally required to open a bank account?" />
                                                                                            </span>
                                                                                            <span className="text">
                                                                                                {formData.countryName && (
                                                                                                    <a
                                                                                                        href={`https://www.google.com/search?q=open+bank+account+requirements+${encodeURIComponent(formData.countryName)}`}
                                                                                                        target="_blank"
                                                                                                        rel="noopener noreferrer"
                                                                                                    >
                                                                                                        (Search for {formData.countryName} requirements)
                                                                                                    </a>
                                                                                                )}
                                                                                            </span>
                                                                                        </div>
                                                                                    </Card.Body>
                                                                                </Card>
                                                                            </Col>
                                                                        </Row>
                                                                    </Col>

                                                                    <Col md={6}>
                                                                        <Card>
                                                                            <Card.Body className="p-3">
                                                                                <div className="icon-text align-items-center">
                                                                                    <span className="icon" aria-hidden="true">
                                                                                        <Image src={icQ} alt="What is the exchange rate to your local currency?" />
                                                                                    </span>
                                                                                    <span className="text">
                                                                                        What is the exchange rate to your local currency?
                                                                                    </span>
                                                                                </div>
                                                                            </Card.Body>
                                                                        </Card>
                                                                        <Row className="sub-card-row">
                                                                            <Col className="justify-content-start">
                                                                                <Card>
                                                                                    <Card.Body>
                                                                                        <div className="icon-text align-items-center">
                                                                                            <span className="icon" aria-hidden="true">
                                                                                                <Image src={icA} alt="What is the exchange rate to your local currency?" />
                                                                                            </span>
                                                                                            <span className="text">
                                                                                                {formData.currency && (
                                                                                                    <a
                                                                                                        href={`https://www.google.com/finance/quote/${formData.currency}-CHF`}
                                                                                                        target="_blank"
                                                                                                        rel="noopener noreferrer"
                                                                                                    >
                                                                                                        View {formData.currency}-CHF rate on Google Finance
                                                                                                    </a>
                                                                                                )}
                                                                                            </span>
                                                                                        </div>
                                                                                    </Card.Body>
                                                                                </Card>
                                                                            </Col>
                                                                        </Row>
                                                                    </Col>
                                                                </Row>

                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb countryName={formData.countryName} subPage="Financials & Banks" />
                        </Col>
                    </Row>

                    {!isMobile ? (
                        <Row>
                            <Col>
                                <Card className="mb-4">
                                    <Card.Body className="pt-0">
                                        <Row>
                                            <Col xs={12}>
                                                <>
                                                    <Row>
                                                        <Col>
                                                            <Card className="card-premium">
                                                                <Card.Body>
                                                                    <Row className="align-items-center">
                                                                        <Col>
                                                                            <h3 className="text-start">Country</h3>
                                                                            <hr />
                                                                        </Col>
                                                                    </Row>

                                                                    <Row className="mb-4">
                                                                        <Col md={8} className="d-flex">
                                                                            <div>
                                                                                <h1>{formData.countryName || '—'}</h1>
                                                                                <p>
                                                                                    {formData.url ? (
                                                                                        <a
                                                                                            href={formData.url.startsWith('http') ? formData.url : `https://${formData.url}`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                        >
                                                                                            {formData.url}
                                                                                        </a>
                                                                                    ) : '—'}
                                                                                    {formData.cityCount > 0 && (
                                                                                        <>
                                                                                            <span className="dot"></span>
                                                                                            Live cities: <strong>{formData.cityCount}</strong>
                                                                                        </>
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-5">
                                                        <Col>
                                                            <NavTabs items={discoveryCountryNavItems} />
                                                        </Col>
                                                    </Row>

                                                    {sectionContent}
                                                </>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                        <div className="country-detail-mobile country-detail-mobile--financials">
                            <CountryDetailHero
                                countryName={formData.countryName}
                                url={formData.url}
                                cityCount={formData.cityCount}
                            />

                            <CountryDetailMobileTabs items={discoveryCountryNavItems} />

                            <Card className="country-detail-content-card mb-4">
                                <Card.Body>
                                    {sectionContent}
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Container>
            </main>
        </>
    );
};

export default DiscoveryCountryViewFinancials;
